import { useEffect, useMemo, useState } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    FormGroup,
    Label,
    Row,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    Table
} from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as moment from "moment";
import { Api } from "../../../componentes/Api";
import "./Reportes.css";

const defaultResumen = {
    efectivo: 0,
    tarjeta: 0,
    credito: 0,
    transferencia: 0,
    cheque: 0,
    cobertura: 0,
    totalVentas: 0
};

const defaultConteo = {
    efectivo: 0,
    tarjeta: 0,
    credito: 0,
    transferencia: 0,
    cheque: 0
};

const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const toMoney = (value) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(toNumber(value));

const Reportec = () => {
    const [fechaInicio, setFechaInicio] = useState(new Date());
    const [fechaFin, setFechaFin] = useState(new Date());
    const [ventas, setVentas] = useState([]);
    const [empresa, setEmpresa] = useState({});
    const [imageSrc, setImageSrc] = useState("");
    const [cssContent, setCssContent] = useState("");
    const [resumen, setResumen] = useState(defaultResumen);
    const [conteo, setConteo] = useState(defaultConteo);
    const [loading, setLoading] = useState(false);
    const [verReporte, setVerReporte] = useState(false);

    const user = JSON.parse(window.localStorage.getItem("sesion_usuario") || "{}");

    const totalEsperado = useMemo(
        () =>
            toNumber(resumen.efectivo) +
            toNumber(resumen.tarjeta) +
            toNumber(resumen.credito) +
            toNumber(resumen.transferencia) +
            toNumber(resumen.cheque),
        [resumen]
    );

    const totalContado = useMemo(
        () =>
            toNumber(conteo.efectivo) +
            toNumber(conteo.tarjeta) +
            toNumber(conteo.credito) +
            toNumber(conteo.transferencia) +
            toNumber(conteo.cheque),
        [conteo]
    );

    const diferencia = totalContado - totalEsperado;

    const cargarEmpresa = async () => {
        if (!user?.empresaID) return;
        const response = await fetch(Api + "Pacientes/Empresa/" + user.empresaID);
        if (response.ok) {
            const data = await response.json();
            setEmpresa(data || {});
        }
    };

    const fetchImage = async () => {
        if (!user?.empresaID) return;
        const response = await fetch(Api + "Administracion/MostraFoto/" + user.empresaID);
        if (!response.ok) return;
        const blob = await response.blob();
        setImageSrc(URL.createObjectURL(blob));
    };

    useEffect(() => {
        fetch("/css/ReportesFacturaSeguro.css")
            .then((response) => response.text())
            .then((text) => setCssContent(text))
            .catch(() => setCssContent(""));
        cargarEmpresa();
        fetchImage();
    }, []);

    const buscarVenta = async () => {
        const fi = moment(fechaInicio).format("YYYY-MM-DD");
        const ff = moment(fechaFin).format("YYYY-MM-DD");
        setLoading(true);

        try {
            const response = await fetch(Api + "ReportesMedical/ReporteTransaccionesDiaria/" + fi + "/" + ff);
            if (!response.ok) {
                setVentas([]);
                setResumen(defaultResumen);
                return;
            }

            const data = await response.json();
            const listado = Array.isArray(data) ? data : [];
            setVentas(listado);

            const calc = listado.reduce(
                (acc, x) => {
                    acc.efectivo += toNumber(x.efectivo);
                    acc.tarjeta += toNumber(x.tarjeta);
                    acc.credito += toNumber(x.cxc);
                    acc.transferencia += toNumber(x.transferencia);
                    acc.cheque += toNumber(x.cheque);
                    acc.cobertura += toNumber(x.montoTotalCobertura);
                    acc.totalVentas += toNumber(x.totalLinea);
                    return acc;
                },
                { ...defaultResumen }
            );

            setResumen(calc);
        } finally {
            setLoading(false);
        }
    };

    const onConteoChange = (e) => {
        const { name, value } = e.target;
        setConteo((prev) => ({ ...prev, [name]: Math.max(0, toNumber(value)) }));
    };

    const limpiarConteo = () => setConteo(defaultConteo);

    const abrirImpresion = () => {
        const contenedor = document.getElementById("reporteCajaPrint");
        if (!contenedor) return;

        const win = window.open("", "", "width=1200,height=800");
        if (!win) return;

        win.document.write("<html><head><title>Cuadre de Caja</title><style>");
        win.document.write(cssContent || "");
        win.document.write(`
            body { font-family: Arial, sans-serif; padding: 12px; }
            .print-head { display: flex; align-items: center; gap: 14px; margin-bottom: 12px; }
            .print-logo { width: 90px; height: auto; }
            .print-summary { display: grid; grid-template-columns: repeat(3, minmax(160px, 1fr)); gap: 8px; margin: 12px 0; }
            .print-item { border: 1px solid #dadada; border-radius: 8px; padding: 8px; }
            .print-item b { display: block; margin-top: 2px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 6px; font-size: 12px; }
            th { background: #f1f4f8; }
            .right { text-align: right; }
        `);
        win.document.write("</style></head><body>");
        win.document.write(contenedor.innerHTML);
        win.document.write("</body></html>");
        win.document.close();

        setTimeout(() => win.print(), 600);
    };

    return (
        <>
            <Row>
                <Col sm={12} className="reportesc-container">
                    <Card className="reportesc-card">
                        <CardHeader className="reportesc-header">
                            <div>
                                <div className="reportesc-title">Reportes de Caja - Consultorio</div>
                                <div className="reportesc-subtitle">Cierre, cuadre e impresion de caja por rango de fechas</div>
                            </div>
                        </CardHeader>
                        <CardBody className="reportesc-body">
                            <Row className="align-items-end reportesc-filters">
                                <Col md={3} sm={6}>
                                    <FormGroup>
                                        <Label className="reportesc-label">Fecha Inicio</Label>
                                        <DatePicker
                                            className="form-control form-control-sm reportesc-input"
                                            selected={fechaInicio}
                                            onChange={(date) => setFechaInicio(date || new Date())}
                                            dateFormat="dd/MM/yyyy"
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md={3} sm={6}>
                                    <FormGroup>
                                        <Label className="reportesc-label">Fecha Fin</Label>
                                        <DatePicker
                                            className="form-control form-control-sm reportesc-input"
                                            selected={fechaFin}
                                            onChange={(date) => setFechaFin(date || new Date())}
                                            dateFormat="dd/MM/yyyy"
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md={2} sm={4}>
                                    <Button className="reportesc-btn reportesc-btn-primary" size="sm" block onClick={buscarVenta} disabled={loading}>
                                        {loading ? "Consultando..." : "Consultar"}
                                    </Button>
                                </Col>

                                <Col md={2} sm={4}>
                                    <Button className="reportesc-btn reportesc-btn-outline" size="sm" block onClick={() => setVerReporte(true)} disabled={ventas.length === 0}>
                                        Ver Cuadre
                                    </Button>
                                </Col>

                                <Col md={2} sm={4}>
                                    <Button className="reportesc-btn reportesc-btn-dark" size="sm" block onClick={abrirImpresion} disabled={ventas.length === 0}>
                                        Imprimir Caja
                                    </Button>
                                </Col>
                            </Row>

                            <Row className="mt-2">
                                <Col md={12}>
                                    <div className="reportesc-summary">
                                        <div className="reportesc-summary-item"><span>Efectivo</span><b>{toMoney(resumen.efectivo)}</b></div>
                                        <div className="reportesc-summary-item"><span>Tarjeta</span><b>{toMoney(resumen.tarjeta)}</b></div>
                                        <div className="reportesc-summary-item"><span>Credito / CxC</span><b>{toMoney(resumen.credito)}</b></div>
                                        <div className="reportesc-summary-item"><span>Transferencia</span><b>{toMoney(resumen.transferencia)}</b></div>
                                        <div className="reportesc-summary-item"><span>Cheque</span><b>{toMoney(resumen.cheque)}</b></div>
                                        <div className="reportesc-summary-item"><span>Cobertura Seguro</span><b>{toMoney(resumen.cobertura)}</b></div>
                                        <div className="reportesc-summary-item"><span>Ventas (Lineas)</span><b>{toMoney(resumen.totalVentas)}</b></div>
                                        <div className="reportesc-summary-item reportesc-summary-item-total"><span>Total Esperado Caja</span><b>{toMoney(totalEsperado)}</b></div>
                                    </div>
                                </Col>
                            </Row>

                            <Card className="mt-3 reportesc-cash-card">
                                <CardHeader className="reportesc-cash-header">Cuadre Manual de Caja</CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col md={2} sm={6}><Label className="reportesc-label">Efectivo contado</Label><Input className="reportesc-input" type="number" min="0" step="0.01" name="efectivo" value={conteo.efectivo} onChange={onConteoChange} /></Col>
                                        <Col md={2} sm={6}><Label className="reportesc-label">Tarjeta contado</Label><Input className="reportesc-input" type="number" min="0" step="0.01" name="tarjeta" value={conteo.tarjeta} onChange={onConteoChange} /></Col>
                                        <Col md={2} sm={6}><Label className="reportesc-label">Credito / CxC</Label><Input className="reportesc-input" type="number" min="0" step="0.01" name="credito" value={conteo.credito} onChange={onConteoChange} /></Col>
                                        <Col md={2} sm={6}><Label className="reportesc-label">Transferencia</Label><Input className="reportesc-input" type="number" min="0" step="0.01" name="transferencia" value={conteo.transferencia} onChange={onConteoChange} /></Col>
                                        <Col md={2} sm={6}><Label className="reportesc-label">Cheque</Label><Input className="reportesc-input" type="number" min="0" step="0.01" name="cheque" value={conteo.cheque} onChange={onConteoChange} /></Col>
                                        <Col md={2} sm={6} className="d-flex align-items-end"><Button className="reportesc-btn reportesc-btn-light" block onClick={limpiarConteo}>Limpiar</Button></Col>
                                    </Row>

                                    <Row className="mt-3 reportesc-balance-row">
                                        <Col md={4}><b>Total esperado: {toMoney(totalEsperado)}</b></Col>
                                        <Col md={4}><b>Total contado: {toMoney(totalContado)}</b></Col>
                                        <Col md={4}>
                                            <b className={diferencia === 0 ? "reportesc-diff-ok" : "reportesc-diff-bad"}>
                                                Diferencia: {toMoney(diferencia)}
                                            </b>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Modal className="modal-preguntas reportesc-modal" isOpen={verReporte}>
                <ModalHeader className="modal-header1">Reporte de Cuadre de Caja</ModalHeader>
                <ModalBody>
                    <div id="reporteCajaPrint" className="mt-2 reportesc-print-wrap">
                        <div className="print-head">
                            {imageSrc ? <img src={imageSrc} alt="Logo" id="logoImg" className="print-logo" /> : null}
                            <div>
                                <h4>{empresa?.descripcion || "Empresa"}</h4>
                                <div>{empresa?.direccion || ""}</div>
                                <div>RNC: {empresa?.rnc || ""}</div>
                                <div>TEL: {empresa?.telefono || ""} {empresa?.email ? `| ${empresa.email}` : ""}</div>
                            </div>
                        </div>

                        <div>
                            <b>Reporte:</b> Cuadre de Caja Consultorio
                            <br />
                            <b>Rango:</b> {moment(fechaInicio).format("DD/MM/YYYY")} - {moment(fechaFin).format("DD/MM/YYYY")}
                            <br />
                            <b>Usuario:</b> {user?.nombre || user?.correo || ""}
                            <br />
                            <b>Generado:</b> {moment(new Date()).format("DD/MM/YYYY HH:mm:ss")}
                        </div>

                        <div className="print-summary">
                            <div className="print-item">Efectivo esperado<b>{toMoney(resumen.efectivo)}</b></div>
                            <div className="print-item">Tarjeta esperada<b>{toMoney(resumen.tarjeta)}</b></div>
                            <div className="print-item">Credito/CxC esperado<b>{toMoney(resumen.credito)}</b></div>
                            <div className="print-item">Transferencia esperada<b>{toMoney(resumen.transferencia)}</b></div>
                            <div className="print-item">Cheque esperado<b>{toMoney(resumen.cheque)}</b></div>
                            <div className="print-item">Total esperado<b>{toMoney(totalEsperado)}</b></div>
                            <div className="print-item">Total contado<b>{toMoney(totalContado)}</b></div>
                            <div className="print-item">Diferencia<b>{toMoney(diferencia)}</b></div>
                            <div className="print-item">Cobertura seguro<b>{toMoney(resumen.cobertura)}</b></div>
                        </div>

                        <Table size="sm" bordered responsive className="reportesc-table">
                            <thead>
                                <tr>
                                    <th>No</th>
                                    <th>Paciente</th>
                                    <th>Servicio</th>
                                    <th className="right">Efectivo</th>
                                    <th className="right">Tarjeta</th>
                                    <th className="right">Credito/CxC</th>
                                    <th className="right">Cobertura</th>
                                    <th className="right">Total Linea</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ventas.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center">Sin datos para el rango seleccionado.</td>
                                    </tr>
                                ) : (
                                    ventas.map((x) => (
                                        <tr key={x.facturaLineaID}>
                                            <td>{x.facturaLineaID}</td>
                                            <td>{x.nombreCliente}</td>
                                            <td>{x.descripcionArticulo}</td>
                                            <td className="right">{toMoney(x.efectivo)}</td>
                                            <td className="right">{toMoney(x.tarjeta)}</td>
                                            <td className="right">{toMoney(x.cxc)}</td>
                                            <td className="right">{toMoney(x.montoTotalCobertura)}</td>
                                            <td className="right">{toMoney(x.totalLinea)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={abrirImpresion}>Imprimir</Button>
                    <Button color="secondary" onClick={() => setVerReporte(false)}>Cerrar</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default Reportec;
